import React from "react";

const SITE_URL = "https://www.nurselinkhub.com";

/**
 * Per-route SEO tags. Uses React 19's native document metadata (title/meta/link
 * auto-hoist to <head>).
 *
 *   <SEO title="Agencies" description="…" path="/agencies" jsonLd={{ … }} />
 *
 * Pass `jsonLd` as an object or an array of objects to inject Schema.org
 * structured data (rendered as a <script type="application/ld+json"> block).
 */
export default function SEO({ title, description, path = "/", image, noindex, jsonLd }) {
  const fullTitle = title ? `${title} · NurseLinkHub` : "NurseLinkHub — verified nurses, agencies, and community";
  const desc = description || "NurseLinkHub is the professional social network for U.S. nurses — verified credentials, transparent agency directory, forums, DMs, events, and polls.";
  const canonical = `${SITE_URL}${path === "/" ? "" : path}`;
  const ogImg = image || `${SITE_URL}/og-default.png`;
  const ldItems = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="NurseLinkHub" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImg} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImg} />
      {ldItems.map((item, i) => (
        <script
          key={i}
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(item) }}
        />
      ))}
    </>
  );
}

/** Standard NurseLinkHub Organization schema — inject on landing + trust pages. */
export const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "NurseLinkHub",
  url: SITE_URL,
  logo: `${SITE_URL}/logo512.png`,
  description: "The professional social network and agency directory for U.S. nurses. Verified credentials, transparent pay data, community forums, direct messaging.",
  sameAs: [
    // Add real profile URLs here as they exist.
  ],
};

/** Website + SearchAction schema — helps Google surface an inline site search. */
export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "NurseLinkHub",
  url: SITE_URL,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/agencies?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

/** Build a JobPosting schema entry from a job document. */
export function jobPostingSchema(job, agency) {
  if (!job || !agency) return null;
  return {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: job.title,
    description: job.description || `${job.title} at ${agency.name}`,
    datePosted: job.created_at,
    employmentType: (job.contract_type || "").toLowerCase().includes("perm") ? "FULL_TIME" : "CONTRACTOR",
    hiringOrganization: {
      "@type": "Organization",
      name: agency.name,
      sameAs: agency.website || undefined,
      logo: agency.logo_url || undefined,
    },
    jobLocation: (job.city || job.state) ? {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressLocality: job.city || undefined,
        addressRegion: job.state || undefined,
        addressCountry: "US",
      },
    } : undefined,
    ...(job.pay_min || job.pay_max ? {
      baseSalary: {
        "@type": "MonetaryAmount",
        currency: "USD",
        value: {
          "@type": "QuantitativeValue",
          minValue: job.pay_min || undefined,
          maxValue: job.pay_max || undefined,
          unitText: (job.pay_unit || "/wk") === "/hr" ? "HOUR"
                  : (job.pay_unit === "/yr" ? "YEAR" : "WEEK"),
        },
      },
    } : {}),
    directApply: !!(job.apply_url || agency.application_url),
    identifier: {
      "@type": "PropertyValue",
      name: "NurseLinkHub",
      value: job.id,
    },
  };
}

/** Build an Article schema entry from a news article document. */
export function newsArticleSchema(article) {
  if (!article) return null;
  return {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: article.title,
    url: article.link || article.url,
    datePublished: article.published_at,
    publisher: {
      "@type": "Organization",
      name: article.source || "NurseLinkHub",
    },
    author: { "@type": "Organization", name: article.source || "NurseLinkHub" },
    articleSection: "Nursing",
  };
}
